'use client'

import { incrementTelemetryHealth } from './telemetry-health'

const drains = new Map<symbol, () => void>()
const MAX_DRAINS = 32
let installed = false

function drainBeforeExit(event: Event): void {
  try {
    if (
      event.type === 'visibilitychange' &&
      document.visibilityState !== 'hidden'
    ) {
      return
    }
    // A callback may register another collector; snapshot the bounded set so
    // lifecycle dispatch cannot grow its own work indefinitely.
    for (const drain of [...drains.values()]) {
      try {
        drain()
      } catch {
        incrementTelemetryHealth('client_lifecycle_failed')
      }
    }
  } catch {
    incrementTelemetryHealth('client_lifecycle_failed')
  }
}

/** Install before RUM init: its final batch uses the same window capture phase. */
export function installBrowserLifecycleDrain(): void {
  if (installed || typeof window === 'undefined') {
    return
  }
  try {
    window.addEventListener('visibilitychange', drainBeforeExit, true)
    window.addEventListener('freeze', drainBeforeExit, true)
    installed = true
  } catch {
    incrementTelemetryHealth('client_lifecycle_failed')
    try {
      window.removeEventListener('visibilitychange', drainBeforeExit, true)
      window.removeEventListener('freeze', drainBeforeExit, true)
    } catch {
      // Unsupported lifecycle hooks cannot prevent SDK or product startup.
    }
  }
}

/** Collectors enqueue completed aggregates only; unfinished business work continues. */
export function registerBrowserLifecycleDrain(drain: () => void): () => void {
  if (drains.size >= MAX_DRAINS) {
    incrementTelemetryHealth('collector_buffer_overflow')
    return () => {}
  }
  const key = Symbol('browser-metric-drain')
  drains.set(key, drain)
  return () => {
    drains.delete(key)
  }
}
